import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Form, Button, Spinner, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { BsPencil, BsTrash } from 'react-icons/bs';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import StarDivider from '../components/uiComponents/StarDivider';
import PortalModal from '../components/PortalModal';
import Toast from '../components/Toast';
import categoryService from '../api/categoryService';
import Pagination from '../components/Pagination';

const TYPE_OPTIONS = [
  { value: 'theme', label: 'Thème', color: '#f3e5f5' },
  { value: 'speaker', label: 'Intervenant', color: '#fce4ec' },
];

const SECTION_OPTIONS = [
  { value: 'semence_matinale', label: 'Semence Matinale' },
  { value: 'enseignements', label: 'Enseignements' },
  { value: 'temoignages', label: 'Témoignages' },
  { value: 'prieres', label: 'Prières' },
  { value: 'motivation_lundi', label: 'Les Lundis de la motivation' },
];

const EMPTY_FORM = { nom: '', type: '', couleur: '', category: '' };
const DEFAULT_CATEGORY_COLOR = '#8B0000';

function extractError(err) {
  if (err?.response?.data?.errors?.length)
    return err.response.data.errors.map(e => e.message).join(', ');
  return err?.response?.data?.message || err?.message || 'Une erreur est survenue';
}

const typeLabel = v => TYPE_OPTIONS.find(t => t.value === v)?.label || v;
const typeBg = v => TYPE_OPTIONS.find(t => t.value === v)?.color || '#eee';

function CategoryListColumn({ title, type, items, loading, pagination, currentPage, onPageChange, onEdit, onDelete, onAdd, isViewer }) {
  if (loading) {
    return (
      <div className="rounded" style={{ border: '1px solid #eee', height: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f8f8' }}>
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <div className="rounded" style={{ border: '1px solid #eee', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '500px', backgroundColor: '#f8f8f8' }}>
      {/* Header - White background */}
      <div className="p-3 py-1 bg-white d-flex justify-content-between align-items-center" style={{ borderBottom: '1px solid #eee', flexShrink: 0 }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>{title}</span>
        {!isViewer && (
          <Button
            size="sm"
            onClick={() => onAdd(type)}
            className='d-flex align-items-center justify-content-center fw-bold'
            style={{ backgroundColor: 'transparent', border: 'none', fontSize: '12px', color:'#8B0000' }}
          >
            + Ajouter
          </Button>
        )}
      </div>

      {/* Content */}
      <div className='hide-scrollbar bg-white' style={{ flex: 1, overflowY: 'auto', padding: '16px', scrollBehavior: 'smooth' }}>
        {items.length === 0 ? (
          <div className="text-secondary text-center" style={{ fontSize: 13, padding: '32px 16px' }}>
            Aucune catégorie
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {items.map(row => (
              <div
                key={row.id}
                className='px-3 rounded-2 py-1 border border-muted d-flex align-items-center justify-content-between gap-3 '
                style={{
                  background:'#f4f4f5 ',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: row.couleur || '#8B0000',
                      flexShrink: 0,
                      boxShadow: `0 0 0 2px ${(row.couleur || '#8B0000')}33`,
                    }}
                  />
                  <OverlayTrigger
                    placement="top"
                    delay={{ show: 400, hide: 100 }}
                    overlay={<Tooltip id={`tip-${row.id}`}>{row.nom}</Tooltip>}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: '#1f2328',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        display: 'block',
                      }}
                    >
                      {row.nom}
                    </span>
                  </OverlayTrigger>
                </div>
                {!isViewer && (
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={() => onEdit(row)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      title="Modifier"
                    >
                      <BsPencil size={14} color="#666" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(row)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      title="Supprimer"
                    >
                      <BsTrash size={14} color="#8B0000" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer with Pagination */}
      <div className="bg-white d-flex align-items-center justify-content-center" style={{ borderTop: '1px solid #eee', flexShrink: 0}}>
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.totalPages || 1}
          onPageChange={onPageChange}
          className={'mt-0 d-flex align-items-center justify-content-center py-3 gap-3'}
        />
      </div>
    </div>
  );
}

export default function CategoriesPage() {
  const isViewer = useSelector((s) => s.auth.user?.role) === 'viewer';
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({});

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ message: '', type: 'success' });
  const [filters, setFilters] = useState({
    theme: { page: 1, limit: 20 },
    speaker: { page: 1, limit: 20 },
    search: '',
    section: '',
  });
  const [filtersOpen, setFiltersOpen] = useState(false);

  const showToast = (message, type = 'success') => setToast({ message, type });
  const hideToast = () => setToast({ message: '', type: 'success' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch themes
      const themeParams = { page: filters.theme.page, limit: filters.theme.limit, type: 'theme' };
      if (filters.search) themeParams.search = filters.search;
      if (filters.section) themeParams.section = filters.section;

      // Fetch speakers
      const speakerParams = { page: filters.speaker.page, limit: filters.speaker.limit, type: 'speaker' };
      if (filters.search) speakerParams.search = filters.search;
      if (filters.section) speakerParams.section = filters.section;

      const [themeRes, speakerRes] = await Promise.all([
        categoryService.list(themeParams),
        categoryService.list(speakerParams),
      ]);

      setData({
        theme: themeRes.data?.data || [],
        speaker: speakerRes.data?.data || [],
      });

      setPagination({
        theme: themeRes.data?.pagination || {},
        speaker: speakerRes.data?.pagination || {},
      });
    } catch (err) {
      showToast(extractError(err), 'danger');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClearFilters = () => {
    setFilters({
      theme: { page: 1, limit: 20 },
      speaker: { page: 1, limit: 20 },
      search: '',
      section: '',
    });
  };

  const openAdd = (type) => {
    setForm({ ...EMPTY_FORM, type });
    setEditItem(null);
    setFormErrors({});
    setShowModal(true);
  };

  const openEdit = row => {
    setForm({ nom: row.nom, type: row.type, couleur: row.couleur || '', category: row.section || '' });
    setEditItem(row);
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditItem(null);
    setForm(EMPTY_FORM);
  };

  const setF = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setFormErrors(fe => ({ ...fe, [k]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!form.nom.trim()) errors.nom = 'Nom requis';
    if (!form.type) errors.type = 'Type requis';
    if (!form.category.trim()) errors.category = 'Section requise';
    return errors;
  };

  const handleSave = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nom: form.nom.trim(),
        type: form.type,
        couleur: form.couleur || DEFAULT_CATEGORY_COLOR,
        section: form.category.trim(),
      };
      if (editItem) {
        await categoryService.update(editItem.id, payload);
        showToast('Catégorie mise à jour avec succès');
      } else {
        await categoryService.create(payload);
        showToast('Catégorie créée avec succès');
      }
      closeModal();
      fetchData();
    } catch (err) {
      showToast(extractError(err), 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await categoryService.remove(deleteTarget.id);
      showToast('Catégorie supprimée avec succès');
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      showToast(extractError(err), 'danger');
      setDeleteTarget(null);
    }
  };

  const themeData = Array.isArray(data.theme) ? data.theme : [];
  const speakerData = Array.isArray(data.speaker) ? data.speaker : [];

  return (
    <div>
      <Toast message={toast.message} type={toast.type} onClose={hideToast} />

      <div className="text-center mb-3">
        <h5 style={{ fontFamily: 'serif', letterSpacing: 3, fontWeight: 400 }}>CATÉGORIES</h5>
        <StarDivider lineWidth="5%" />
      </div>

      {/* Filters */}
      <div className="bg-white p-3 rounded mb-4" style={{ border: '1px solid #eee' }}>
        <div
          className="d-md-none d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer', marginBottom: filtersOpen ? 12 : 0 }}
          onClick={() => setFiltersOpen(v => !v)}
        >
          <span className="fw-semibold" style={{ fontSize: 14 }}>
            Filtres de recherche
          </span>
          <span>{filtersOpen ? <FaChevronUp size={18} /> : <FaChevronDown size={18} />}</span>
        </div>
        <div className={filtersOpen ? '' : 'd-none d-md-block'}>
          <Row>
            <Col md={10} xs={12}>
              <Row className="gy-1 gx-3">
                <Col md={6} xs={12}>
                  <div>
                    <Form.Label className="small fw-semibold d-block mb-1">Section</Form.Label>
                    <Form.Select
                      size="sm"
                      value={filters.section}
                      onChange={e =>
                        setFilters(f => ({
                          ...f,
                          section: e.target.value,
                          theme: { ...f.theme, page: 1 },
                          speaker: { ...f.speaker, page: 1 },
                        }))
                      }
                    >
                      <option value="">Toutes les sections</option>
                      {SECTION_OPTIONS.map(t => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                </Col>
                <Col md={6} xs={12}>
                  <div>
                    <Form.Label className="small fw-semibold d-block mb-1">Recherche</Form.Label>
                    <Form.Control
                      size="sm"
                      value={filters.search}
                      onChange={e =>
                        setFilters(f => ({
                          ...f,
                          search: e.target.value,
                          theme: { ...f.theme, page: 1 },
                          speaker: { ...f.speaker, page: 1 },
                        }))
                      }
                      placeholder="Nom de catégorie..."
                    />
                  </div>
                </Col>
              </Row>
            </Col>
            <Col md={2} xs={12} className="d-flex flex-wrap align-items-end justify-content-md-end justify-content-center gap-2 mt-2 mt-md-0">
              {(filters.search || filters.section) && (
                <Button size="sm" variant="outline-secondary" onClick={handleClearFilters} className="text-uppercase px-2">
                  EFFACER
                </Button>
              )}
            </Col>
          </Row>
        </div>
      </div>

      {/* Two Column Layout */}
      <Row className="g-4">
        <Col lg={6} xs={12}>
          <CategoryListColumn
            title="Thème"
            type="theme"
            items={themeData}
            loading={loading}
            pagination={pagination.theme || {}}
            currentPage={filters.theme.page}
            onPageChange={page => setFilters(f => ({ ...f, theme: { ...f.theme, page } }))}
            onEdit={openEdit}
            onDelete={row => setDeleteTarget(row)}
            onAdd={openAdd}
            isViewer={isViewer}
          />
        </Col>
        <Col lg={6} xs={12}>
          <CategoryListColumn
            title="Intervenant"
            type="speaker"
            items={speakerData}
            loading={loading}
            pagination={pagination.speaker || {}}
            currentPage={filters.speaker.page}
            onPageChange={page => setFilters(f => ({ ...f, speaker: { ...f.speaker, page } }))}
            onEdit={openEdit}
            onDelete={row => setDeleteTarget(row)}
            onAdd={openAdd}
            isViewer={isViewer}
          />
        </Col>
      </Row>

      {/* Type Selection Modal - REMOVED */}

      {/* Add/Edit Modal */}
      <PortalModal
        show={showModal}
        onClose={closeModal}
        title={editItem ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
        size="sm"
      >
        <Form.Group className="mb-3">
          <Form.Label className="modal-label">Nom :</Form.Label>
          <Form.Control
            value={form.nom}
            onChange={e => setF('nom', e.target.value)}
            placeholder="Ex: Foi et espérance"
            required
            isInvalid={!!formErrors.nom}
            style={{ marginTop: 4 }}
          />
          <Form.Control.Feedback type="invalid">{formErrors.nom}</Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="modal-label">Section :</Form.Label>
          <Form.Select
            size="sm"
            value={form.category}
            onChange={e => setF('category', e.target.value)}
            required
            isInvalid={!!formErrors.category}
            style={{ marginTop: 4 }}
          >
            <option value="">Sélectionner une section</option>
            {SECTION_OPTIONS.map(t => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">{formErrors.category}</Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="modal-label">Couleur (optionnelle) :</Form.Label>
          <div className="d-flex align-items-center gap-2 mt-1">
            <input
              type="color"
              value={form.couleur || DEFAULT_CATEGORY_COLOR}
              onChange={e => setF('couleur', e.target.value)}
              style={{
                width: 44,
                height: 36,
                padding: 2,
                border: '1px solid #ccc',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            />
            <Form.Control
              value={form.couleur}
              onChange={e => setF('couleur', e.target.value)}
              placeholder={`${DEFAULT_CATEGORY_COLOR} (par défaut)`}
              style={{ fontSize: 13 }}
            />
          </div>
        </Form.Group>

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <button
            className="btn-primary-mcm"
            onClick={handleSave}
            disabled={saving}
            style={{
              minWidth: 180,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            {saving ? <Spinner size="sm" animation="border" /> : editItem ? 'ENREGISTRER LES MODIFICATIONS' : 'ENREGISTRER'}
          </button>
        </div>
      </PortalModal>

      {/* Delete Confirmation Modal */}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Body style={{ padding: 28 }}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: '#ffe5e5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 10px',
              }}
            >
              <BsTrash size={22} color="#8B0000" />
            </div>
            <h6 style={{ fontWeight: 700, marginBottom: 2 }}>Supprimer cette catégorie ?</h6>
            <p style={{ fontSize: 13, color: '#666', margin: 0 }}>Cette action est irréversible.</p>
          </div>
          {deleteTarget && (
            <div style={{ backgroundColor: '#f7f8fa', borderRadius: 8, padding: '14px 16px', marginBottom: 24, fontSize: 13 }}>
              <div className="mb-2" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {deleteTarget.couleur && (
                  <span
                    style={{
                      display: 'inline-block',
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      backgroundColor: deleteTarget.couleur,
                      flexShrink: 0,
                    }}
                  />
                )}
                <span className="fw-semibold" style={{ fontSize: 14 }}>
                  {deleteTarget.nom}
                </span>
              </div>
              <div>
                <span className="fw-semibold">Type : </span>
                <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: 11, backgroundColor: typeBg(deleteTarget.type) }}>
                  {typeLabel(deleteTarget.type)}
                </span>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={() => setDeleteTarget(null)}
              style={{
                padding: '8px 24px',
                borderRadius: 4,
                border: '1px solid #ccc',
                background: '#fff',
                cursor: 'pointer',
                fontSize: 13,
                letterSpacing: 0.5,
              }}
            >
              ANNULER
            </button>
            <button
              onClick={handleDelete}
              style={{
                padding: '8px 24px',
                borderRadius: 4,
                border: 'none',
                backgroundColor: '#8B0000',
                color: '#fff',
                cursor: 'pointer',
                fontSize: 13,
                letterSpacing: 0.5,
              }}
            >
              SUPPRIMER
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
